import axios from 'axios';
import LocalStorageService from '@/scripts/localStorage.js';
import { router } from '@/router.js';
import store from '@/stores/index.js';
import { default as buildODataQuery } from 'odata-query';

/**
 * @typedef {import('odata-query').QueryOptions} ODataQueryOptions
 * @property {Object} [customQueryParams] - Custom query parameters
 */

const localStorageService = LocalStorageService.getService();
const instanceFile = axios.create();
let isRefreshing = false;
let arrRequestRetry = [];

export const api = {
    /**
     * @param {import('axios').AxiosRequestConfig} config
     */
    ajax: function(config) {
        config.url = apiBaseUrl + config.url;
        return axios(config);
    },

    get: function(url, controllerSignal) {
        return axios.get(apiBaseUrl + url, { signal: controllerSignal });
    },

    put: function(url, request) {
        return axios.put(apiBaseUrl + url, request);
    },

    putInstance: function(url, request, config) {
        return instanceFile.put(url, request, config);
    },

    post: function(url, request) {
        return axios.post(apiBaseUrl + url, request);
    },

    postBuffer: function(url, request) {
        return axios({
        url: apiBaseUrl + url,
        method: 'POST',
        data: request,
        headers: {
          'Content-Type': 'application/json',
        },
        responseType: 'arraybuffer',
      })
    },

    getBuffer: function(url) {
        return axios({
            url: apiBaseUrl + url,
            method: 'GET',
            responseType: 'arraybuffer',
        });
    },

    patch: function(url, changes) {
        var request = [];

        for (var i = 0; i < changes.length; i++) {
            request.push({
                op: 'replace',
                path: '/' + changes[i].name,
                value: changes[i].value,
            });
        }

        return axios({
            method: 'patch',
            url: apiBaseUrl + url,
            headers: {
                'Content-Type': 'application/json-patch+json',
            },
            data: JSON.stringify(request),
        });
    },

    delete: function(url, request, signal) {
        if (request) {
            return axios({
                method: 'delete',
                url: apiBaseUrl + url,
                data: request,
                signal,
            });
        } else {
            return axios.delete(apiBaseUrl + url, {
                signal
            });
        }
    },

    /** @deprecated */ 
    odataPatch: function(url, request) {
        return axios({
            method: 'patch',
            url: apiBaseUrl + url,
            headers: {
                'Content-Type': 'application/json',
            },
            data: JSON.stringify(request),
        });
    },

    msPatch: function(url, request) {
        return axios({
            method: 'patch',
            url: apiBaseUrl + url,
            headers: {
                'Content-Type': 'application/json',
            },
            data: JSON.stringify(request),
        });
    },

    applyODataParams(service, config = {}) {
        const paramList = [
            'custom' in config && config.custom ? config.custom : null,
            'expand' in config && config.expand ? `expand=${config.expand}` : null,
            'filter' in config && config.filter ? `filter=${config.filter}` : null,
            'orderBy' in config && config.orderBy ? `orderby=${config.orderBy}` : null,
            'search' in config && config.search ? `search=${config.search}` : null,
            'select' in config && config.select ? `select=${config.select}` : null,
            'skip' in config && config.skip ? `skip=${config.skip}` : null,
            'top' in config && config.top ? `top=${config.top}` : null,
            'count' in config && config.count ? 'count=true' : null,
        ].filter(Boolean);
        const noOdata = config.noOdata ? config.noOdata : ''
        const paramConfigStr = paramList.length > 0 ? `${noOdata}&$${paramList.join('&$')}` : null;


        return [service, paramConfigStr].filter(Boolean).join('?');
    },

    applyNodeDataParams(service, config = {}) {
        const paramList = [
            'filter' in config && config.filter ? `where=${config.filter}` : null,
            'skip' in config && config.skip ? `offset=${config.skip}` : null,
            'limit' in config && config.limit ? `limit=${config.limit}` : null,
            'order' in config && config.order ? `order=[${config.order}]` : null,
            'include' in config && config.include ? `include=${config.include}` : null,
            'attributes' in config && config.attributes ? `attributes=${encodeURIComponent(JSON.stringify(config.attributes))}` : null,
            'custom' in config && config.custom ? config.custom : null
        ].filter(Boolean);

        const paramConfigStr = paramList.length > 0 ? `${paramList.join('&')}` : null;
        return [service, paramConfigStr].filter(Boolean).join('?');
    },

    getNodeDataParams(config = {}) {
        const params = {};

        const paramList = [
            'filter' in config && config.filter ? params.where = `${config.filter}` : null,
            'skip' in config && config.skip ? params.offset = `${config.skip}` : null,
            'limit' in config && config.limit ? params.limit = `${config.limit}` : null,
            'order' in config && config.order ? params.order =`[${config.order}]` : null,
            'include' in config && config.include ? params.include = `${config.include}` : null,
            'attributes' in config && config.attributes ? params.attributes = `${encodeURIComponent(JSON.stringify(config.attributes))}` : null,
            'custom' in config && config.custom ? params.custom = config.custom : null
        ].filter(Boolean);

        return params;
    },

    getFetch: function(url) {
        const token = localStorageService.getAccessToken();
        return fetch( apiBaseUrl + url, {
            method: 'get',
            credentials: 'include',
            headers: {
                'Content-Type': 'application/json',
                 Authorization: `Bearer ${token}`
            },
        });
    },
    isCanceled(error) {
        return axios.isCancel(error);
    },

    /**
     * Builds an OData query string from the provided options.
     *
     * @param {Partial<ODataQueryOptions>} options
     */
    buildODataQuery(options) {
        let queryParams = buildODataQuery(options);

        if(options.customQueryParams) {
            const customQueryParams = Object.entries(options.customQueryParams)
                .filter(([_, value]) => value != null)
                .map(([key, value]) => `${key}=${value}`);
            queryParams += (queryParams ? '&' : '?') + customQueryParams.join('&');
        }

        return queryParams;
    },
};

//Interceptors

axios.interceptors.request.use(
    (config) => {
        const token = localStorageService.getAccessToken();
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        Promise.reject(error);
    }
);

axios.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        const originalRequest = error.config;
        let responseObj={statusCode: '', message: ''}

        if(axios.isCancel(error)) {
            return Promise.reject(error);
        }

        if(error.config.responseType==='arraybuffer'){
            const decodedString = String.fromCharCode.apply(null, new Uint8Array(error.response.data));
            responseObj = JSON.parse(decodedString)
        }

        if ((responseObj.statusCode === 401 || error.response.status === 401) &&
            (
                responseObj.message === 'Invalid JWT.' ||
                error.response.data.message === 'Invalid JWT.' ||
                error.response.data.Message === 'Authorization has been denied for this request.' ||
            ( error.response.data.error &&
                error.response.data.error.message === 'Authorization has been denied for this request.')   
            )
        ) {
            if (!isRefreshing) {
                isRefreshing = true;
                axios
                    .post(`${apiBaseUrl}/ms/auth/v1/auth/refresh`, {
                        token: localStorageService.getRefreshToken(),
                    })
                    .then((res) => {
                        localStorageService.setToken(res.data.data);
                        arrRequestRetry.forEach((pendingRequest) => {
                            pendingRequest.onSuccess();
                        });
                    })
                    .catch(() => {
                        const loginDestinationRouteBeforeRepeatedLogout =  store.state.loginDestinationRoute;
                        store.dispatch('logout').then(() => {
                            if (loginDestinationRouteBeforeRepeatedLogout) {
                                // Logout clears loginDestinationRoute, so remember the original when logout is called repeatedly
                                store.commit('setLoginDestinationRoute', loginDestinationRouteBeforeRepeatedLogout);
                            } else {
                                // Refresh token was too old to renew session, redirect to original destination after login
                                const fullRoute = [window.location.pathname, window.location.search, window.location.hash]
                                    .filter(Boolean)
                                    .join('');
                                store.commit('setLoginDestinationRoute', fullRoute);
                            }
                            router.push('/Account/Login');
                        });
                    })
                    .finally(() => {
                        arrRequestRetry = [];
                        isRefreshing = false;
                    });
            }
            return new Promise((resolve, reject) => {
                arrRequestRetry.push({
                    onSuccess: () => {
                        resolve(axios(originalRequest));
                    },
                    onFailure: (error) => {
                        reject(error);
                    },
                });
            });
        }
        return Promise.reject(error);
    }
);
